import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { MainLayout } from '@/layouts/MainLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';

import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DepartmentDashboardPage } from '@/pages/DepartmentDashboardPage';
import { ExecutiveDashboardPage } from '@/pages/ExecutiveDashboardPage';
import { TaskListPage } from '@/pages/TaskListPage';
import { TaskDetailPage } from '@/pages/TaskDetailPage';
import { TaskFormPage } from '@/pages/TaskFormPage';
import { TaskTreePage } from '@/pages/TaskTreePage';
import { SigningListPage } from '@/pages/SigningListPage';
import { SigningDetailPage } from '@/pages/SigningDetailPage';
import { SigningCreatePage } from '@/pages/SigningCreatePage';
import { ReportsPage } from '@/pages/ReportsPage';
import { KpiReportPage } from '@/pages/KpiReportPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { UsersPage } from '@/pages/UsersPage';
import { ChatPage } from '@/pages/ChatPage';
import { DocumentsListPage } from '@/pages/DocumentsListPage';
import { DocumentCategoriesPage } from '@/pages/DocumentCategoriesPage';
import { AdminCategoriesPage } from '@/pages/AdminCategoriesPage';
import { DocumentDetailPage } from '@/pages/DocumentDetailPage';
import { AuditLogPage } from '@/pages/AuditLogPage';
import { SystemSettingsPage } from '@/pages/SystemSettingsPage';
import { PrintPreviewPage } from '@/pages/PrintPreviewPage';
import { TemplatesAdminPage } from '@/pages/TemplatesAdminPage';
import { TemplatesPickerPage, TemplateFillPage } from '@/pages/TemplatesPickerPage';
import { PaperFillPage } from '@/pages/PaperFillPage';
import { TemplateBuilderPage } from '@/pages/TemplateBuilderPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import {
  DepartmentsCategoryPage,
  TaskCategoriesCategoryPage,
  PrioritiesCategoryPage,
  LabelsCategoryPage,
  PrintTemplatesCategoryPage,
  RolesCategoryPage,
} from '@/pages/CategoryPages';
import { DeptGroupsPage } from '@/pages/DeptGroupsPage';
// V1.0.37: Module Hộp thư lắng nghe
import { FeedbackPublicPage } from '@/pages/FeedbackPublicPage';
import { FeedbackTopicsPage } from '@/pages/admin/FeedbackTopicsPage';
import { FeedbackTopicFormPage } from '@/pages/admin/FeedbackTopicFormPage';
import { FeedbackInboxPage } from '@/pages/admin/FeedbackInboxPage';
// V1.0.64: Voice Recording
import VoiceDashboardPage from '@/pages/voice/VoiceDashboardPage';
import RecordingDevicesAdminPage from '@/pages/voice/RecordingDevicesAdminPage';
import VoiceDeviceTimelinePage from '@/pages/voice/VoiceDeviceTimelinePage';
import VoiceSearchPage from '@/pages/voice/VoiceSearchPage';

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* V1.0.37: Hộp thư lắng nghe public — no auth (bệnh nhân quét QR) */}
      <Route path="/feedback" element={<FeedbackPublicPage />} />

      {/* Print preview - full screen, no layout */}
      <Route
        path="/print/:docId"
        element={
          <ProtectedRoute>
            <PrintPreviewPage />
          </ProtectedRoute>
        }
      />

      {/* Protected app */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/department" element={<DepartmentDashboardPage />} />
        <Route
          path="/dashboard/executive"
          element={
            <ProtectedRoute requireRoles={['BOD']}>
              <ExecutiveDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route path="/tasks" element={<TaskListPage />} />
        <Route path="/tasks/new" element={<TaskFormPage />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        <Route path="/tasks/:id/edit" element={<TaskFormPage />} />
        <Route path="/tasks/:id/tree" element={<TaskTreePage />} />

        {/* Project management — Khoa → Project → Tasks */}
        <Route path="/projects" element={<ProjectsPage />} />

        <Route path="/signing" element={<SigningListPage />} />
        <Route path="/signing/new" element={<SigningCreatePage />} />
        <Route path="/signing/:id" element={<SigningDetailPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:id" element={<ChatPage />} />
        <Route path="/documents" element={<DocumentsListPage />} />
        <Route path="/documents/categories" element={<DocumentCategoriesPage />} />
        <Route path="/documents/:id" element={<DocumentDetailPage />} />

        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/kpi" element={<KpiReportPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Admin */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN']}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN']}>
              <AdminCategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN']}>
              <SystemSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* V1.0.37: Module Hộp thư lắng nghe — admin/BOD/feedback:view_inbox */}
        <Route
          path="/admin/feedback/topics"
          element={
            <FeedbackAdminRoute>
              <FeedbackTopicsPage />
            </FeedbackAdminRoute>
          }
        />
        <Route
          path="/admin/feedback/topics/new"
          element={
            <FeedbackAdminRoute>
              <FeedbackTopicFormPage />
            </FeedbackAdminRoute>
          }
        />
        <Route
          path="/admin/feedback/topics/:id/edit"
          element={
            <FeedbackAdminRoute>
              <FeedbackTopicFormPage />
            </FeedbackAdminRoute>
          }
        />
        <Route
          path="/admin/feedback/inbox"
          element={
            <FeedbackAdminRoute>
              <FeedbackInboxPage />
            </FeedbackAdminRoute>
          }
        />

        {/* Document templates - admin */}
        <Route
          path="/admin/templates"
          element={
            <ProtectedRoute requirePermissions={['template:manage']}>
              <TemplatesAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/templates/:id/builder"
          element={
            <ProtectedRoute requirePermissions={['template:manage']}>
              <TemplateBuilderPage />
            </ProtectedRoute>
          }
        />
        {/* Document templates - user picker + fill */}
        <Route
          path="/templates"
          element={
            <ProtectedRoute requirePermissions={['template:use']}>
              <TemplatesPickerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/:id/fill"
          element={
            <ProtectedRoute requirePermissions={['template:use']}>
              <TemplateFillPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/:id/paper-fill"
          element={
            <ProtectedRoute requirePermissions={['template:use']}>
              <PaperFillPage />
            </ProtectedRoute>
          }
        />

        {/* Categories (admin + dept_head) */}
        <Route
          path="/categories/departments"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <DepartmentsCategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/dept-groups"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <DeptGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/task-categories"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <TaskCategoriesCategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/priorities"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <PrioritiesCategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/labels"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <LabelsCategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/print-templates"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN', 'DEPT_HEAD', 'BOD']}>
              <PrintTemplatesCategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/roles"
          element={
            <ProtectedRoute requireRoles={['SYS_ADMIN']}>
              <RolesCategoryPage />
            </ProtectedRoute>
          }
        />

        {/* V1.0.64: Voice Recording */}
        <Route
          path="/voice-records/dashboard"
          element={
            <VoiceRoute>
              <VoiceDashboardPage />
            </VoiceRoute>
          }
        />
        <Route
          path="/voice-records/device/:deviceCode"
          element={
            <VoiceRoute>
              <VoiceDeviceTimelinePage />
            </VoiceRoute>
          }
        />
        <Route
          path="/voice-search"
          element={
            <VoiceRoute>
              <VoiceSearchPage />
            </VoiceRoute>
          }
        />
        <Route
          path="/admin/recording-devices"
          element={
            <ProtectedRoute requirePermissions={['recording:manage_devices']}>
              <RecordingDevicesAdminPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * V1.0.37: Wrapper cho routes hộp thư lắng nghe.
 * Cho phép: SYS_ADMIN, BOD HOẶC có permission feedback:view_inbox.
 */
function FeedbackAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasAnyRole, hasPermission } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // OR logic: admin/BOD HOẶC có permission feedback:view_inbox
  const canAccess = hasAnyRole(['SYS_ADMIN', 'BOD']) || hasPermission('feedback:view_inbox');
  if (!canAccess) {
    return (
      <Result
        status="403"
        title="403 - Không có quyền truy cập"
        subTitle="Bạn cần quyền 'Xem hộp thư lắng nghe' hoặc vai trò SYS_ADMIN/BOD."
        extra={<Button type="primary" onClick={() => window.history.back()}>Quay lại</Button>}
      />
    );
  }
  return <>{children}</>;
}

/**
 * V1.0.64: Wrapper cho routes voice recording.
 * Cho phép: SYS_ADMIN, BOD HOẶC có permission recording:view_all / recording:view_dept.
 */
function VoiceRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasAnyRole, hasPermission } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const canAccess =
    hasAnyRole(['SYS_ADMIN', 'BOD']) ||
    hasPermission('recording:view_all') ||
    hasPermission('recording:view_dept') ||
    hasPermission('recording:manage_devices');
  if (!canAccess) {
    return (
      <Result
        status="403"
        title="403 - Không có quyền truy cập"
        subTitle="Bạn cần quyền 'recording:view_*' hoặc vai trò SYS_ADMIN/BOD để xem ghi âm."
        extra={<Button type="primary" onClick={() => window.history.back()}>Quay lại</Button>}
      />
    );
  }
  return <>{children}</>;
}
